import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GAP = 4;
const VIEWPORT_MARGIN = 8;

// Dropdown panel themed from the Review display palette, like the widget's
// sort/filter popovers (secondary button colours, primary on the active item).
//
// Not Polaris' Popover: that renders into <body>, outside .portal, where the
// palette CSS variables don't reach. This one renders into the .portal root, with
// position: fixed so a parent's overflow (cards clip) can't cut it off.
//
// alignment: "left" | "right" — which edge of the activator the panel lines up with.
// className: modifier on the panel (e.g. portal-popover--name-format).
const PortalPopover = ({
    active,
    activator,
    onClose,
    alignment = "right",
    className = "",
    children,
}) => {
    const anchorRef = useRef(null);
    const panelRef = useRef(null);
    const [position, setPosition] = useState(null);

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        const panel = panelRef.current;
        if (!anchor || !panel) return;

        const rect = anchor.getBoundingClientRect();
        const width = panel.offsetWidth;
        const height = panel.offsetHeight;

        let left = alignment === "right" ? rect.right - width : rect.left;
        left = Math.max(
            VIEWPORT_MARGIN,
            Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN)
        );

        // Below the activator; above it when there's no room underneath
        let top = rect.bottom + GAP;
        if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
            top = Math.max(VIEWPORT_MARGIN, rect.top - GAP - height);
        }
        setPosition({ top, left });
    }, [alignment]);

    useLayoutEffect(() => {
        if (active) updatePosition();
        else setPosition(null);
    }, [active, updatePosition]);

    useEffect(() => {
        if (!active) return;
        const handlePointerDown = (e) => {
            if (
                anchorRef.current?.contains(e.target) ||
                panelRef.current?.contains(e.target)
            ) {
                return;
            }
            onClose?.();
        };
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        // Follow the activator while any scroll container moves
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [active, onClose, updatePosition]);

    const container =
        anchorRef.current?.closest(".portal") || document.body;

    return (
        <span className="portal-popover__anchor" ref={anchorRef}>
            {activator}
            {active &&
                createPortal(
                    <div
                        ref={panelRef}
                        className={`portal-popover ${className}`}
                        role="menu"
                        style={
                            position
                                ? { top: position.top, left: position.left }
                                : { visibility: "hidden", top: 0, left: 0 }
                        }
                        // Clicks inside must not reach handlers behind (e.g. the
                        // write-review overlay closing on click)
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                    </div>,
                    container
                )}
        </span>
    );
};

// Menu items for PortalPopover: [{ content, onAction, prefix, destructive, active }]
export const PortalActionList = ({ items }) => (
    <div className="portal-popover__list">
        {items.map((item, index) => (
            <button
                key={item.id ?? item.content ?? index}
                type="button"
                role="menuitem"
                className={`portal-popover__item ${
                    item.active ? "is-active" : ""
                } ${item.destructive ? "is-destructive" : ""}`}
                onClick={item.onAction}
            >
                {item.prefix && (
                    <span className="portal-popover__item-prefix">
                        {item.prefix}
                    </span>
                )}
                {item.content}
            </button>
        ))}
    </div>
);

export default PortalPopover;
